import { useState, useEffect } from "react";
import AccordionSection from "./AccordionSection.jsx";
import TextureGrid from "./TextureGrid.jsx";
import { wardrobeDesigns, wardrobeTextures } from "../data/wardrobeData.js";
import { designPricing, wardrobeTextureGroups } from "../data/pricingData.js";

const DEFAULT_TEXTURE = "/textures/Alpine White W1100 ST9.jpg";
const SETTINGS = window.VMWardrobeConfigurator || {};
const USE_DYNAMIC_DATA =
  SETTINGS.useDynamicData === true || SETTINGS.dataSource === "dynamic";

function termById(terms = [], id) {
  const numericId = Number(id);
  return terms.find((term) => Number(term.id) === numericId) ?? null;
}

function configSize(item) {
  return Number(
    item.measurements?.total_size ||
      parseFloat(item.properties?.total_wardrobe_size) ||
      parseFloat(item.properties?.wardrobe_size) ||
      item.size ||
      0,
  );
}

function DesignCard({ thumbnail, hoverThumbnail, label, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  const image = hovered && hoverThumbnail ? hoverThumbnail : thumbnail;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-center gap-1.5 w-full"
    >
      <div
        className={`w-full aspect-square overflow-hidden border transition-colors ${
          selected ? "bg-gray-100 border-gray-900" : "bg-gray-50 border-transparent hover:border-gray-300"
        }`}
      >
        {image && (
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover"
            onError={(event) => {
              event.currentTarget.hidden = true;
            }}
          />
        )}
      </div>
      <span className="text-xs text-gray-600 text-center leading-tight">
        {label}
      </span>
    </button>
  );
}

function WardrobeCard({ design, selected, onClick }) {
  return (
    <DesignCard
      thumbnail={design.thumbnail}
      hoverThumbnail={design.thumbnailHover}
      label={design.specs?.productName || design.label}
      selected={selected}
      onClick={onClick}
    />
  );
}

function DynamicProductCard({ item, selected, onClick }) {
  const shape = termById(item.shapes, item.properties?.shape_id);
  const thumbnail = item.product?.thumbnail || "";
  const hoverThumbnail = item.product?.thumbnailHover || "";
  // The static card caption is the shape plus size, e.g. "L-Shape (5LM)" —
  // not the full product name.
  const size = Number(item.measurements?.total_size || 0);
  const shapeName = shape?.name || item.product?.name || "Wardrobe";
  const label = size > 0 ? `${shapeName} (${size}LM)` : shapeName;

  return (
    <DesignCard
      thumbnail={thumbnail}
      hoverThumbnail={hoverThumbnail}
      label={label}
      selected={selected}
      onClick={onClick}
    />
  );
}

function colorPlaceholder(hex = "#e5e7eb") {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="${hex || "#e5e7eb"}"/></svg>`,
  )}`;
}

function colorToTexture(color) {
  const textureUrl = color.texture?.url || color.thumbnail || "";
  return {
    id: String(color.id),
    colorId: Number(color.id),
    label: color.name,
    path: textureUrl || DEFAULT_TEXTURE,
    thumbnail: color.thumbnail || textureUrl || colorPlaceholder(),
    grade: color.grade || "g1",
  };
}

function colorsFor(config) {
  return (config?.colors?.wood ?? []).map(colorToTexture);
}

function dynamicColorGrade(config, colorId) {
  const numericId = Number(colorId);
  const color = (config?.colors?.wood ?? []).find((item) => Number(item.id) === numericId);
  const grade = color?.grade || "g1";
  return grade === "none" ? "g1" : grade;
}

function calculateDynamicPrice(config, cartConfig) {
  if (!config || !cartConfig) return 0;
  const grade = dynamicColorGrade(config, cartConfig.wood_color_id);
  // Color-wise pricing: selected color grade (G1/G2/G3) selects wood matrix rate.
  return (
    Number(config.base_price || 0) +
    Number(config.price_matrix?.wood?.[grade] || 0)
  );
}

function firstTexture(textures) {
  return textures[0] ?? null;
}

export default function Configurator(props) {
  return USE_DYNAMIC_DATA ? (
    <DynamicConfigurator {...props} />
  ) : (
    <StaticConfigurator {...props} />
  );
}

function DynamicConfigurator({
  onModelSelect,
  onTextureChange,
  onPriceChange,
  onInfoChange,
  onCartConfigChange,
  onLoadingChange,
}) {
  const [productConfigs, setProductConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedTextureId, setSelectedTextureId] = useState("");
  // One section open at a time, as in the static app: picking a design closes
  // the design list and opens Wood Color.
  const [openSection, setOpenSection] = useState("design");

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    let alive = true;

    async function loadProducts() {
      setLoading(true);
      setError("");
      try {
        if (!window.VMWardrobe?.getProducts) {
          throw new Error("Dynamic data helper is not available.");
        }
        const items = await window.VMWardrobe.getProducts();
        const configs = await Promise.all(
          items.map((item) => window.VMWardrobe.getProductConfig(item.id)),
        );
        if (!alive) return;
        setProductConfigs(configs);
        if (configs.length === 0) setError("No dynamic wardrobe products found.");
      } catch (loadError) {
        if (!alive) return;
        setError(loadError.message || "Unable to load dynamic wardrobe products.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadProducts();
    return () => {
      alive = false;
    };
  }, []);

  const designConfigs = [...productConfigs]
    .sort((a, b) => configSize(a) - configSize(b));
  const config =
    designConfigs.find((item) => Number(item.product?.id) === Number(selectedProductId)) ?? null;
  const woodTextures = colorsFor(config);
  const selectedTexture = woodTextures.find((texture) => texture.id === selectedTextureId);

  useEffect(() => {
    if (!config) {
      onModelSelect?.(null);
      onInfoChange?.(null);
      onPriceChange?.(0);
      onCartConfigChange?.(null);
      return;
    }

    const texture = firstTexture(woodTextures);
    setSelectedTextureId(texture?.id ?? "");
    onTextureChange?.(texture?.path ?? DEFAULT_TEXTURE);
    onModelSelect?.(config.model?.url || null);
    onInfoChange?.({
      dynamic: true,
      productConfig: config,
      design: {
        id: config.product?.article_number || String(config.product?.id ?? ""),
        label: termById(config.shapes, config.properties?.shape_id)?.name || config.product?.name || "Wardrobe",
        size: configSize(config),
      },
    });
    setOpenSection("woodColor");
  }, [config?.product?.id]);

  const cartConfig = config
    ? {
        product_id: Number(config.product.id),
        shape_id: Number(config.properties?.shape_id || 0),
        wood_color_id: Number(selectedTextureId || 0),
        quantity: 1,
      }
    : null;

  useEffect(() => {
    onCartConfigChange?.(cartConfig && cartConfig.wood_color_id > 0 ? cartConfig : null);
  }, [config?.product?.id, selectedTextureId]);

  useEffect(() => {
    onPriceChange?.(calculateDynamicPrice(config, cartConfig));
  }, [config?.product?.id, selectedTextureId]);

  function handleDesignSelect(item) {
    setSelectedProductId(item.product.id);
  }

  function handleTextureSelect(texture) {
    setSelectedTextureId(texture.id);
    onTextureChange?.(texture.path);
  }

  if (loading) {
    return <div className="bg-white h-full" />;
  }

  if (error) {
    return (
      <div className="bg-white h-full grid place-items-center p-8 text-sm text-red-500 text-center">
        {error}
      </div>
    );
  }

  if (productConfigs.length === 0) {
    return (
      <div className="bg-white h-full grid place-items-center p-8 text-sm text-gray-500 text-center">
        No dynamic wardrobe products found. Enable Wardrobe Configurator on a published simple product.
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-center text-sm font-bold tracking-[0.2em] uppercase text-gray-900">
          Personalize Your Wardrobe
        </h1>
      </div>

      <div className="border-t border-gray-200">
        <AccordionSection
          title="Wardrobe Design"
          isOpen={openSection === "design"}
          onToggle={() =>
            setOpenSection((value) => (value === "design" ? null : "design"))
          }
        >
          <div className="grid grid-cols-4 gap-2 px-5 pb-5 pt-4">
            {designConfigs.length === 0 ? (
              <p className="col-span-4 py-4 text-center text-xs text-gray-400">
                No designs available
              </p>
            ) : designConfigs.map((item) => (
              <DynamicProductCard
                key={item.product.id}
                item={item}
                selected={Number(selectedProductId) === Number(item.product.id)}
                onClick={() => handleDesignSelect(item)}
              />
            ))}
          </div>
        </AccordionSection>

        {config && (
          <AccordionSection
            title="Wood Color"
            isOpen={openSection === "woodColor"}
            onToggle={() =>
              setOpenSection((value) =>
                value === "woodColor" ? null : "woodColor",
              )
            }
          >
            <TextureGrid
              textures={woodTextures}
              selectedId={selectedTextureId}
              onSelect={handleTextureSelect}
            />
            {!selectedTexture && (
              <p className="px-5 pb-5 text-xs text-red-500">Please add common wardrobe colors in admin.</p>
            )}
          </AccordionSection>
        )}
      </div>
    </div>
  );
}

function StaticConfigurator({
  onModelSelect,
  onTextureChange,
  onPriceChange,
  onInfoChange,
  onCartConfigChange,
}) {
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [selectedTextureId, setSelectedTextureId] = useState("alpine-white");
  const [woodColorOpen, setWoodColorOpen] = useState(true);
  const filteredDesigns = [...wardrobeDesigns].sort((a, b) => a.size - b.size);
  const hasDesign = selectedDesignId !== null;

  const totalPrice = (() => {
    if (!hasDesign) return 0;
    const pricing = designPricing[selectedDesignId];
    if (!pricing) return 0;
    const group = wardrobeTextureGroups[selectedTextureId] ?? "g1";
    return pricing[group] ?? 0;
  })();

  useEffect(() => {
    onPriceChange?.(totalPrice);
  }, [totalPrice]);

  useEffect(() => {
    onCartConfigChange?.(null);
  }, [onCartConfigChange]);

  function handleDesignSelect(design) {
    setSelectedDesignId(design.id);
    onModelSelect?.(design.model);
    onInfoChange?.(design);
    setWoodColorOpen(true);
  }

  function handleTextureSelect(texture) {
    setSelectedTextureId(texture.id);
    onTextureChange?.(texture.path);
  }

  return (
    <div className="bg-white">
      <div className="border-t border-gray-200">
        <AccordionSection title="Wardrobe Design" defaultOpen>
          <div className="grid grid-cols-4 gap-2 px-5 pb-5 pt-4">
            {filteredDesigns.map((design) => (
              <WardrobeCard
                key={design.id}
                design={design}
                selected={selectedDesignId === design.id}
                onClick={() => handleDesignSelect(design)}
              />
            ))}
          </div>
        </AccordionSection>

        {hasDesign && (
          <AccordionSection
            title="Wood Color"
            isOpen={woodColorOpen}
            onToggle={() => setWoodColorOpen((value) => !value)}
          >
            <TextureGrid
              textures={wardrobeTextures}
              selectedId={selectedTextureId}
              onSelect={handleTextureSelect}
            />
          </AccordionSection>
        )}
      </div>
    </div>
  );
}
